import React from "react";
import { Button } from "flowbite-react";
import {
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
// import { useTranslation } from "react-i18next";

interface PaginationProps {
  currentPage: number; // 1-based
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  showItemsPerPage?: boolean;
  loading?: boolean;
  customItemsPerPageOptions?: number[];
  className?: string;
}

const DEFAULT_ITEMS_PER_PAGE_OPTIONS = [10, 20, 25, 30, 50, 100];
const MAX_VISIBLE_PAGES = 5;

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  loading = false,
  customItemsPerPageOptions,
  className,
}) => {
  const { t } = useTranslation();
  const itemsPerPageOptions =
    customItemsPerPageOptions || DEFAULT_ITEMS_PER_PAGE_OPTIONS;

  // Calculate entry range
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  // Calculate visible page range
  const getVisiblePages = () => {
    const pages: number[] = [];
    const halfMaxPages = Math.floor(MAX_VISIBLE_PAGES / 2);
    let startPage = Math.max(1, currentPage - halfMaxPages);
    const endPage = Math.min(totalPages, startPage + MAX_VISIBLE_PAGES - 1);
    if (endPage - startPage + 1 < MAX_VISIBLE_PAGES) {
      startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };
  const visiblePages = getVisiblePages();

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 bg-white  w-full ${
        className || ""
      }`}
    >
      <div className="flex items-center gap-2 text-sm text-gray-700">
        <span>
          {t("apps.pagination.showing", {
            startIndex,
            endIndex,
            totalItems,
          })}
          {/* Showing {startIndex} to {endIndex} of {totalItems} entries */}
        </span>
        {showItemsPerPage && (
          <>
            <span className="ml-2">|</span>
            <span>{t("apps.pagination.show")}</span>
            <select
              value={itemsPerPage}
              onChange={(e) =>
                onItemsPerPageChange &&
                onItemsPerPageChange(Number(e.target.value))
              }
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block p-1.5"
              disabled={loading}
            >
              {itemsPerPageOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>{t("common.itemsPerPage")}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          color="light"
          size="xs"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1 || loading}
        >
          <CaretDoubleLeft size={16} />
        </Button>
        <Button
          color="light"
          size="xs"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          <CaretLeft size={16} />
        </Button>
        {visiblePages.map((page) => (
          <Button
            key={page}
            color={currentPage === page ? "primary" : "light"}
            size="xs"
            onClick={() => onPageChange(page)}
            disabled={loading}
          >
            {page}
          </Button>
        ))}
        <Button
          color="light"
          size="xs"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
        >
          <CaretRight size={16} />
        </Button>
        <Button
          color="light"
          size="xs"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || loading}
        >
          <CaretDoubleRight size={16} />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
